import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Volume2, VolumeX, ShoppingBag, User, LayoutDashboard, LogOut, Package, MapPin } from "lucide-react";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/lib/i18n";

export const Nav = ({ muted, onToggleMute }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const accountRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
        scrolled ? "bg-obsidian/80 backdrop-blur-md border-b border-gold/10" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 sm:px-12 lg:px-20 h-20">
        <Link to="/" data-testid="nav-logo-button" aria-label="DEO ATELIER home">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {[
            [t("nav.winkel"), "/winkel"],
            [t("nav.merken"), "/winkel"],
            [t("nav.huis"), "/#huis"],
          ].map(([label, path], i) => (
            <button
              key={label}
              data-testid={`nav-link-${path.replace(/[\/#]/g, "") || "home"}`}
              onClick={() => {
                if (path.startsWith("/#")) {
                  navigate("/");
                  setTimeout(() => document.querySelector(path.slice(1))?.scrollIntoView({ behavior: "smooth" }), 80);
                } else {
                  navigate(path);
                }
              }}
              className="font-mono text-[11px] tracking-[0.3em] uppercase text-mist hover:text-gold transition-colors duration-300"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitch className="mr-1" />
          <button
            data-testid="audio-toggle-button"
            onClick={onToggleMute}
            aria-label={muted ? t("nav.musicOn") : t("nav.musicOff")}
            className="hidden sm:flex items-center gap-2 border border-gold/30 rounded-full px-4 py-2 text-gold hover:bg-gold/10 transition-colors duration-300"
          >
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>

          <div className="relative" ref={accountRef}>
            <button
              data-testid="nav-account-button"
              onClick={() => setAccountOpen((o) => !o)}
              aria-label="Account"
              className="flex items-center justify-center h-10 w-10 rounded-full border border-cream/15 text-cream hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <User size={16} />
            </button>
            {accountOpen && (
              <div data-testid="nav-account-menu" className="absolute right-0 top-12 w-56 border border-gold/20 bg-elevate/95 backdrop-blur-md shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)] py-2 z-50">
                {user ? (
                  <>
                    <div className="px-4 py-2 border-b border-gold/10">
                      <p className="text-sm text-cream truncate">{user.name}</p>
                      <p className="font-mono text-[10px] text-smoke truncate">{user.email}</p>
                    </div>
                    {[
                      [<Package size={14} key="p"/>, t("nav.profile"), "/account"],
                      [<MapPin size={14} key="m"/>, t("nav.orders"), "/account/bestellingen"],
                    ].map(([icon, label, path]) => (
                      <button key={label} data-testid={`nav-menu-${path.replace(/\//g, "") || "account"}`} onClick={() => { setAccountOpen(false); navigate(path); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-mist hover:text-gold hover:bg-gold/5 transition-colors">
                        {icon} {label}
                      </button>
                    ))}
                    {user.role === "admin" && (
                      <button data-testid="nav-menu-admin" onClick={() => { setAccountOpen(false); navigate("/admin"); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gold hover:bg-gold/5 transition-colors">
                        <LayoutDashboard size={14} /> {t("nav.admin")}
                      </button>
                    )}
                    <button data-testid="nav-menu-logout" onClick={async () => { setAccountOpen(false); await logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-mist hover:text-red-300 hover:bg-red-500/5 border-t border-gold/10 transition-colors">
                      <LogOut size={14} /> {t("nav.logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <button data-testid="nav-menu-login" onClick={() => { setAccountOpen(false); navigate("/inloggen"); }} className="w-full px-4 py-2.5 text-sm text-cream hover:text-gold hover:bg-gold/5 transition-colors">{t("nav.login")}</button>
                    <button data-testid="nav-menu-register" onClick={() => { setAccountOpen(false); navigate("/registreren"); }} className="w-full px-4 py-2.5 text-sm text-mist hover:text-gold hover:bg-gold/5 transition-colors">{t("nav.register")}</button>
                  </>
                )}
              </div>
            )}
          </div>

          <button
            data-testid="nav-cart-button"
            onClick={() => navigate("/winkelwagen")}
            aria-label={t("cart.eyebrow")}
            className="relative flex items-center justify-center h-10 w-10 rounded-full border border-cream/15 text-cream hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <ShoppingBag size={16} />
            {count > 0 && (
              <span data-testid="cart-badge" className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold text-obsidian font-mono text-[10px] px-1">
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            className="md:hidden flex flex-col justify-center gap-1.5 h-10 w-10 items-center"
          >
            <span className={`h-px w-6 bg-cream transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-[3.5px]" : ""}`} />
            <span className={`h-px w-6 bg-cream transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-gold/10 bg-obsidian/95 backdrop-blur-md px-6 py-4 flex flex-col gap-1">
          {[
            [t("nav.winkel"), "/winkel"],
            [t("nav.service"), "/faq"],
            [t("nav.huis"), "/#huis"],
          ].map(([label, path]) => (
            <button key={label} data-testid={`mobile-nav-${path.replace(/[\/#]/g, "")}`} onClick={() => go(path)} className="text-left py-3 font-mono text-xs tracking-[0.3em] uppercase text-mist hover:text-gold border-b border-gold/5 transition-colors">
              {label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

export default Nav;
